import { Directive, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { ProjectSection } from '../models/access.models';
import { AccessService } from '../services/access.service';

/**
 * Ролевая модель (F2): авто-дизейбл реактивной формы для read-only ролей.
 *
 * Матчится на ЛЮБОЙ `[formGroup]` (отдельные шаблоны править не нужно). Секцию берёт
 * из `data.section` маршрута (её выставляет SectionPermissionGuard, наследуется в дочерние
 * роуты благодаря `paramsInheritanceStrategy: 'always'`). Если нет права `<section>:edit` —
 * вся форма disabled (поля неактивны, valueChanges не шлются → нет авто-записи и 403).
 *
 * Работает только в модулях, импортирующих AccessUiModule.
 */
@Directive({ selector: '[formGroup]' })
export class PermFormDirective implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();
  private _weDisabled = false;

  constructor(
    @Optional() @Self() private _fgd: FormGroupDirective,
    private _route: ActivatedRoute,
    private _access: AccessService,
  ) {}

  ngOnInit(): void {
    const data = this._route.snapshot.data ?? {};
    const section = data['section'] as ProjectSection | undefined;
    // Страницы, где read-only дизейбл не нужен (например, выбор сценариев = запуск расчёта).
    if (!section || data['skipPermDisable'] || !this._fgd) return;

    this._access
      .can$(section, 'edit')
      .pipe(takeUntil(this._destroy$))
      .subscribe((canEdit) => {
        const form = this._fgd.control;
        if (!form) return;
        if (!canEdit) {
          form.disable({ emitEvent: false });
          this._weDisabled = true;
        } else if (this._weDisabled) {
          form.enable({ emitEvent: false });
          this._weDisabled = false;
        }
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
